import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  AddSuppressionPayload,
  CreateTemplatePayload,
  NotificationTemplate,
  PhoneSuppression,
  SendPayload,
  SendResult,
  SetPreferencePayload,
  UpdateNotificationTypePayload,
  UpdateTemplatePayload,
} from '@/client/types/campaigns';
import { campaignsKeys } from './campaigns.query';

const MSG = '/api/admin/v2/messaging';
const USERS = '/api/admin/v2/users';
const TIMEOUT = { timeout: 45_000 };
// A send waits on every recipient's provider call, and a cold API worker can
// add most of a minute; giving up sooner reports a send that went through.
const SEND_TIMEOUT = { timeout: 120_000 };

const unwrap = async <T>(promise: Promise<APIResponse<T>>): Promise<T> => {
  const res = await promise;
  return res.data as T;
};

const invalidateSends = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: campaignsKeys.sends() });
};

const invalidateTemplates = (qc: ReturnType<typeof useQueryClient>, id?: number) => {
  qc.invalidateQueries({ queryKey: campaignsKeys.templates() });
  if (id) qc.invalidateQueries({ queryKey: campaignsKeys.templateDetail(id) });
};

const invalidateSuppressions = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: campaignsKeys.suppressions() });
};

const invalidateNotifTypes = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: campaignsKeys.notifTypes() });
};

const invalidateUser = (qc: ReturnType<typeof useQueryClient>, uid: number) => {
  qc.invalidateQueries({ queryKey: campaignsKeys.user(uid) });
};

// ─── Ad-hoc send ──────────────────────────────────────────────────────────────

export const useCampaignSendMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { body: SendPayload; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<SendResult>>(`${MSG}/send`, 'POST', args.body, {
          ...SEND_TIMEOUT,
          idempotencyKey: args.idempotencyKey || generateIdempotencyKey(),
        }),
      );
    },
    'Failed to send message',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateSends(qc),
  });
};

// ─── Templates ────────────────────────────────────────────────────────────────

export const useCreateTemplateMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { body: CreateTemplatePayload; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean; template?: NotificationTemplate }>>(
          `${MSG}/templates`,
          'POST',
          args.body,
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to create template',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateTemplates(qc),
  });
};

export const useUpdateTemplateMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      ntmplid: number;
      body: UpdateTemplatePayload;
      idempotencyKey?: string;
    }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean; template?: NotificationTemplate }>>(
          `${MSG}/templates/${args.ntmplid}`,
          'PATCH',
          args.body,
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to update template',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { ntmplid }) => invalidateTemplates(qc, ntmplid),
  });
};

export const useDeleteTemplateMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { ntmplid: number; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean }>>(
          `${MSG}/templates/${args.ntmplid}`,
          'DELETE',
          {},
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to delete template',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { ntmplid }) => invalidateTemplates(qc, ntmplid),
  });
};

// ─── Suppressions ─────────────────────────────────────────────────────────────

export const useAddSuppressionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { body: AddSuppressionPayload; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean; suppression?: PhoneSuppression }>>(
          `${MSG}/suppressions`,
          'POST',
          args.body,
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to add SMS suppression',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateSuppressions(qc),
  });
};

export const useRemoveSuppressionMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: { psid: number; idempotencyKey?: string }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean }>>(
          `${MSG}/suppressions/${args.psid}`,
          'DELETE',
          {},
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to remove SMS suppression',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateSuppressions(qc),
  });
};

// ─── Notification types ───────────────────────────────────────────────────────

export const useUpdateNotificationTypeMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      ntypid: number;
      body: UpdateNotificationTypePayload;
      idempotencyKey?: string;
    }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean }>>(
          `${MSG}/notification-types/${args.ntypid}`,
          'PATCH',
          args.body,
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to update notification type',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => invalidateNotifTypes(qc),
  });
};

// ─── Per-user preferences ─────────────────────────────────────────────────────

export const useSetUserPreferenceMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  const fn = withErrorHandling(
    async (args: {
      uid: number;
      body: SetPreferencePayload;
      idempotencyKey?: string;
    }) => {
      return unwrap(
        request<APIResponse<{ success?: boolean; unpid?: number }>>(
          `${USERS}/${args.uid}/notification-preferences`,
          'PUT',
          args.body,
          { ...TIMEOUT, idempotencyKey: args.idempotencyKey || generateIdempotencyKey() },
        ),
      );
    },
    'Failed to update preference',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: (_d, { uid }) => invalidateUser(qc, uid),
  });
};
